/**
 * Le semis : l'animation de bulles peinte sur les `canvas.placeholder`, ce qui
 * s'affiche partout où une carte n'a pas d'image.
 *
 * CE FICHIER A VÉCU EN CINQ EXEMPLAIRES, un par thème, longs de 375 lignes et
 * n'écartant que neuf littéraux de couleur. Le motif de la duplication était écrit
 * dans le fichier lui-même : « ces bulles sont peintes sur un CANEVAS, hors
 * d'atteinte de toute feuille de style : la couleur doit vivre ici ».
 *
 * Le constat vaut pour le PINCEAU, pas pour le SCRIPT — `getComputedStyle` atteint
 * parfaitement les jetons du document. La palette est donc redescendue là où elle
 * a toujours eu sa place, dans le socle de chaque thème (`--semis`), et le fichier
 * est remonté ici, où les thèmes le partagent comme le reste de `@hyperio`.
 */

/**
 * Les teintes du semis, telles que le thème les déclare.
 *
 * Lu UNE FOIS par instance, à la construction : `getComputedStyle` force un calcul
 * de style, et il y a un canevas par carte sans image — jusqu'à douze sur un index.
 * Le jeton ne bouge pas en cours de page, donc rien ne justifie de le relire à
 * chaque image.
 */
function lireSemis() {
    const brut = getComputedStyle(document.documentElement).getPropertyValue('--semis');
    return brut.split(',').map(t => t.trim()).filter(Boolean);
}

class FloatingOrbsAnimation {
    constructor(canvas) {
        this.canvas = canvas;
        this.ctx = canvas.getContext('2d', {
            alpha: true,
            desynchronized: true,
            willReadFrequently: false
        });
        this.orbs = [];
        this.animationFrame = null;
        this.time = 0;
        this.lastFrameTime = performance.now();
        this.isVisible = true;
        this.isHovered = false;

        // Caches pour optimisation
        this.colorCache = new Map();
        this.gradientCache = new Map();
        this.offscreenCache = new Map();

        // Configuration
        this.config = {
            orbCountBase: 50,                  // Nombre de base de particules (sera ajusté selon la taille)
            minSize: 8,                        // Taille minimale (particules plus grandes pour effet bokeh)
            maxSize: 30,                       // Taille maximale (grandes pour effet bokeh)
            minBlur: 0,                        // Flou minimal (net pour premier plan)
            maxBlur: 20,                       // Flou maximal (très fort pour arrière-plan)
            minOpacity: 0.3,                   // Opacité minimale
            maxOpacity: 0.7,                   // Opacité maximale
            moveSpeed: 0.3,                    // Vitesse de déplacement
            // La palette du THÈME, lue au document. Aucun repli codé en dur : un thème
            // sans `--semis` doit se voir tout de suite, et une liste vide donne un canevas
            // nu plutôt qu'un semis aux couleurs d'un autre.
            colors: lireSemis(),
        };

        this.init();
        this.setupVisibilityDetection();
        this.setupHoverDetection();
    }

    init() {
        // Ajuster la taille du canvas
        this.resize();

        // Debounce du resize pour éviter les appels excessifs
        let resizeTimeout;
        this.resizeHandler = () => {
            clearTimeout(resizeTimeout);
            resizeTimeout = setTimeout(() => this.resize(), 100);
        };
        window.addEventListener('resize', this.resizeHandler);

        // Créer les cercles
        this.createOrbs();

        // Démarrer l'animation
        this.animate();
    }

    setupVisibilityDetection() {
        if ('IntersectionObserver' in window) {
            this.observer = new IntersectionObserver((entries) => {
                entries.forEach(entry => {
                    this.isVisible = entry.isIntersecting;
                    if (this.isVisible && this.isHovered && !this.animationFrame) {
                        this.lastFrameTime = performance.now();
                        this.animate();
                    }
                });
            }, { threshold: 0 });
            this.observer.observe(this.canvas);
        }
    }

    setupHoverDetection() {
        // Pointer events : couvre souris + stylet. Sur tactile, pas de hover,
        // donc l'animation reste figée — c'est le comportement voulu (économie batterie).
        this.pointerEnterHandler = () => {
            this.isHovered = true;
            if (this.isVisible && !this.animationFrame) {
                this.lastFrameTime = performance.now();
                this.animate();
            }
        };
        this.pointerLeaveHandler = () => {
            this.isHovered = false;
            // La boucle animate() s'arrêtera d'elle-même au prochain tour.
        };
        this.canvas.addEventListener('pointerenter', this.pointerEnterHandler);
        this.canvas.addEventListener('pointerleave', this.pointerLeaveHandler);
    }

    resize() {
        const rect = this.canvas.getBoundingClientRect();
        const dpr = window.devicePixelRatio || 1;

        // Vérifier que le canvas a des dimensions valides
        if (rect.width === 0 || rect.height === 0) {
            return;
        }

        // Stocker les dimensions pour l'animation (toujours, même si inchangées)
        this.width = rect.width;
        this.height = rect.height;

        // Ne redimensionner que si les dimensions ont vraiment changé
        const newWidth = Math.round(rect.width * dpr);
        const newHeight = Math.round(rect.height * dpr);

        if (this.canvas.width === newWidth && this.canvas.height === newHeight) {
            return;
        }

        this.canvas.width = newWidth;
        this.canvas.height = newHeight;

        // Réinitialiser la transformation avant d'appliquer le scale
        this.ctx.setTransform(1, 0, 0, 1, 0, 0);
        this.ctx.scale(dpr, dpr);

        // Recréer les orbs avec les nouvelles dimensions si elles ont déjà été créées
        if (this.orbs.length > 0) {
            this.gradientCache.clear();
            this.offscreenCache.clear();
            this.createOrbs();
        }
    }

    createOrbs() {
        this.orbs = [];
        const { orbCountBase, colors } = this.config;

        // Calculer le nombre de particules avec une réduction moins drastique pour les petits canvas
        // Base: 50 particules pour ~320x200 (64000 px²)
        const baseArea = 64000;
        const currentArea = this.width * this.height;

        // Pour les petits canvas, on utilise une racine carrée pour réduire moins fortement
        // Ex: 64x64 (4096px²) → sqrt(4096/64000) = 0.253 → 50 * 0.253 = ~13 particules (au lieu de 3)
        const areaRatio = currentArea / baseArea;
        const particleRatio = areaRatio < 0.5 ? Math.sqrt(areaRatio) : areaRatio;
        const orbCount = Math.max(8, Math.round(orbCountBase * particleRatio));

        // Adapter les tailles en fonction de la plus petite dimension du canvas
        const minDimension = Math.min(this.width, this.height);
        const scaleFactor = minDimension / 200; // Facteur basé sur une référence de 200px

        // Pour les petits canvas, garder des tailles plus proportionnelles
        // Ex: 64px → scaleFactor = 0.32, mais on applique une racine pour conserver plus de taille
        const sizeScaleFactor = scaleFactor < 0.5 ? Math.sqrt(scaleFactor) * 0.8 : scaleFactor;

        const minSize = Math.max(3, 8 * sizeScaleFactor);
        const maxSize = Math.max(minSize + 3, 30 * sizeScaleFactor);
        const minBlur = 0;
        const maxBlur = Math.max(3, 20 * sizeScaleFactor);
        const minOpacity = 0.3;
        const maxOpacity = 0.7;

        // Adapter l'amplitude d'oscillation : conserver un minimum pour garder du mouvement
        const baseOscillationAmplitude = Math.max(10, 20 * scaleFactor);
        const oscillationVariation = Math.max(8, 15 * scaleFactor);

        for (let i = 0; i < orbCount; i++) {
            this.orbs.push({
                // Position initiale aléatoire
                x: Math.random() * this.width,
                y: Math.random() * this.height,

                // Taille et apparence
                size: minSize + Math.random() * (maxSize - minSize),
                blur: minBlur + Math.random() * (maxBlur - minBlur),
                opacity: minOpacity + Math.random() * (maxOpacity - minOpacity),
                color: colors[Math.floor(Math.random() * colors.length)],

                // Mouvement : direction aléatoire
                angle: Math.random() * Math.PI * 2,
                speed: 0.2 + Math.random() * 0.3,

                // Oscillation pour effet de va-et-vient (adaptée à la taille)
                oscillationX: Math.random() * Math.PI * 2,
                oscillationY: Math.random() * Math.PI * 2,
                oscillationSpeedX: 0.2 + Math.random() * 0.3,
                oscillationSpeedY: 0.2 + Math.random() * 0.3,
                oscillationAmplitudeX: baseOscillationAmplitude + Math.random() * oscillationVariation,
                oscillationAmplitudeY: baseOscillationAmplitude + Math.random() * oscillationVariation,
            });
        }
    }

    createOrbOffscreen(orb) {
        const cacheKey = `${orb.color}_${orb.size}_${orb.opacity}_${orb.blur}`;

        if (this.offscreenCache.has(cacheKey)) {
            return this.offscreenCache.get(cacheKey);
        }

        const margin = Math.ceil(orb.blur * 4);
        const size = Math.ceil(orb.size * 2 + margin * 2);
        const center = size / 2;

        let offscreen;
        let offCtx;

        if (typeof OffscreenCanvas !== 'undefined') {
            offscreen = new OffscreenCanvas(size, size);
            offCtx = offscreen.getContext('2d', {
                alpha: true,
                willReadFrequently: false
            });
        } else {
            offscreen = document.createElement('canvas');
            offscreen.width = size;
            offscreen.height = size;
            offCtx = offscreen.getContext('2d', {
                alpha: true,
                willReadFrequently: false
            });
        }

        offCtx.filter = `blur(${orb.blur}px)`;

        const gradient = offCtx.createRadialGradient(center, center, 0, center, center, orb.size);
        const color = this.hexToRgba(orb.color, orb.opacity);
        const transparent = this.hexToRgba(orb.color, 0);

        gradient.addColorStop(0, color);
        gradient.addColorStop(0.7, color);
        gradient.addColorStop(1, transparent);

        offCtx.fillStyle = gradient;
        offCtx.beginPath();
        offCtx.arc(center, center, orb.size, 0, Math.PI * 2);
        offCtx.fill();

        offCtx.filter = 'none';

        const result = { canvas: offscreen, offset: center };
        this.offscreenCache.set(cacheKey, result);
        return result;
    }

    drawOrb(orb) {
        const oscillationX = Math.sin(this.time * orb.oscillationSpeedX + orb.oscillationX) * orb.oscillationAmplitudeX;
        const oscillationY = Math.sin(this.time * orb.oscillationSpeedY + orb.oscillationY) * orb.oscillationAmplitudeY;

        const x = orb.x + oscillationX;
        const y = orb.y + oscillationY;

        if (!orb.offscreenCache) {
            orb.offscreenCache = this.createOrbOffscreen(orb);
        }

        const { canvas, offset } = orb.offscreenCache;
        this.ctx.drawImage(canvas, x - offset, y - offset);
    }

    hexToRgba(hex, alpha) {
        const cacheKey = `${hex}_${alpha}`;
        if (this.colorCache.has(cacheKey)) {
            return this.colorCache.get(cacheKey);
        }

        const r = parseInt(hex.slice(1, 3), 16);
        const g = parseInt(hex.slice(3, 5), 16);
        const b = parseInt(hex.slice(5, 7), 16);
        const result = `rgba(${r}, ${g}, ${b}, ${alpha})`;

        this.colorCache.set(cacheKey, result);
        return result;
    }

    updateOrb(orb) {
        // Déplacement lent et continu
        orb.x += Math.cos(orb.angle) * orb.speed * this.config.moveSpeed;
        orb.y += Math.sin(orb.angle) * orb.speed * this.config.moveSpeed;

        // Rebond sur les bords avec une marge pour éviter de voir les cercles sortir
        const margin = orb.size + orb.oscillationAmplitudeX;

        if (orb.x < -margin || orb.x > this.width + margin) {
            orb.angle = Math.PI - orb.angle;
        }
        if (orb.y < -margin || orb.y > this.height + margin) {
            orb.angle = -orb.angle;
        }

        // Garder les cercles dans les limites (avec marge)
        orb.x = Math.max(-margin, Math.min(this.width + margin, orb.x));
        orb.y = Math.max(-margin, Math.min(this.height + margin, orb.y));
    }

    animate() {
        const now = performance.now();
        const deltaTime = (now - this.lastFrameTime) / 1000;
        this.lastFrameTime = now;

        this.ctx.clearRect(0, 0, this.width, this.height);

        const len = this.orbs.length;
        for (let i = 0; i < len; i++) {
            const orb = this.orbs[i];
            this.drawOrb(orb);
            this.updateOrb(orb);
        }

        this.time += deltaTime;

        // On replanifie uniquement si le canvas est visible ET survolé,
        // sinon on rend une frame figée jusqu'au prochain hover.
        if (this.isVisible && this.isHovered) {
            this.animationFrame = requestAnimationFrame(() => this.animate());
        } else {
            this.animationFrame = null;
        }
    }

    destroy() {
        if (this.animationFrame) {
            cancelAnimationFrame(this.animationFrame);
        }
        if (this.resizeHandler) {
            window.removeEventListener('resize', this.resizeHandler);
        }
        if (this.observer) {
            this.observer.disconnect();
        }
        if (this.pointerEnterHandler) {
            this.canvas.removeEventListener('pointerenter', this.pointerEnterHandler);
        }
        if (this.pointerLeaveHandler) {
            this.canvas.removeEventListener('pointerleave', this.pointerLeaveHandler);
        }
        this.colorCache.clear();
        this.gradientCache.clear();
        this.offscreenCache.clear();
    }
}

// Initialiser toutes les animations au chargement
function initPlaceholderAnimations() {
    const placeholders = document.querySelectorAll('canvas.placeholder');
    const animations = [];

    placeholders.forEach(canvas => {
        animations.push(new FloatingOrbsAnimation(canvas));
    });

    return animations;
}

// Auto-initialisation
if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initPlaceholderAnimations);
} else {
    initPlaceholderAnimations();
}

// Export pour utilisation en module
export { FloatingOrbsAnimation, initPlaceholderAnimations };
